import React from 'react';
import { Group, useBreakpoint } from '@landr/maestro';
import { Sample } from 'types';
import { PackState } from 'types/enums';
import TakeDownInfo from 'components/TakeDownInfo/TakeDownInfo';
import { usePreviewContext } from '../PreviewContext';
import { getTwoArrays } from '../helpers/getTwoArrays';
import { usePreviewKeyBoardContext } from '../hooks/keyboardsEvents/usePreviewKeyBoardProvider';
import PreviewSamplesRow from './PreviewSamplesRow';
import PreviewSamplesSkeleton from './PreviewSamplesSkeleton';
import * as Styled from './styled';

export const _PreviewSamples = ({
    columnLeft,
    columnRight,
    showColumnRight,
    RowComponent,
    currentPreviewIndexFocused,
    columnLeftStartingIndex,
    columnRightStartingIndex,
}: {
    columnLeft?: Sample[];
    columnRight?: Sample[];
    showColumnRight: boolean;
    RowComponent: ({
        sample,
        isFocused,
        rowIndex,
    }: {
        sample: Sample;
        isFocused: boolean;
        rowIndex: number;
    }) => JSX.Element;
    currentPreviewIndexFocused: number;
    columnLeftStartingIndex: number;
    columnRightStartingIndex: number;
}) => (
    <Styled.PreviewSamplesWrapper>
        <Styled.FlexStretch>
            <Styled.PreviewSamplesColumn>
                <Group direction="vertical" gutter={0}>
                    {columnLeft?.map((sample: Sample, index: number) => {
                        return (
                            <RowComponent
                                key={sample.guid}
                                sample={sample}
                                isFocused={
                                    index + columnLeftStartingIndex ===
                                    currentPreviewIndexFocused
                                }
                                rowIndex={index + columnLeftStartingIndex}
                            />
                        );
                    })}
                </Group>
            </Styled.PreviewSamplesColumn>

            {showColumnRight && (
                <Styled.PreviewSamplesColumn>
                    <Group direction="vertical" gutter={0}>
                        {columnRight?.map((sample: Sample, index: number) => {
                            return (
                                <RowComponent
                                    key={sample.guid}
                                    sample={sample}
                                    isFocused={
                                        index + columnRightStartingIndex ===
                                        currentPreviewIndexFocused
                                    }
                                    rowIndex={index + columnRightStartingIndex}
                                />
                            );
                        })}
                    </Group>
                </Styled.PreviewSamplesColumn>
            )}
        </Styled.FlexStretch>
    </Styled.PreviewSamplesWrapper>
);

const PreviewSamples = () => {
    const { preview, samples } = usePreviewContext();
    const showColumnRight = !useBreakpoint('lg');
    const { currentPreviewIndexFocused } = usePreviewKeyBoardContext();

    if (!preview || !samples || !samples?.items) {
        return <PreviewSamplesSkeleton showColumnRight={showColumnRight} />;
    }

    if (preview.entity.packState === PackState.TakenDown) {
        return (
            <Styled.PreviewSamplesWrapper>
                <TakeDownInfo size="small" />
            </Styled.PreviewSamplesWrapper>
        );
    }

    const [first, second] = getTwoArrays<Sample>(samples.items, 4);

    // For preview keyboard navigation (arrow up/down):
    // The previewKeyboardEvents hooks will compare those index's and the currentPreviewIndexFocused to start autoplay.
    // Starting at 1 skip the Preview player at the top of the left column and 5 will jump to right column.
    const columnLeftStartingIndex = 1;
    const columnRightStartingIndex = 5;

    return (
        <_PreviewSamples
            columnLeft={first}
            columnRight={second}
            showColumnRight={showColumnRight}
            RowComponent={PreviewSamplesRow}
            currentPreviewIndexFocused={currentPreviewIndexFocused}
            columnLeftStartingIndex={columnLeftStartingIndex}
            columnRightStartingIndex={columnRightStartingIndex}
        />
    );
};

export default PreviewSamples;
